import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useCSVDownloader } from 'react-papaparse'
import { ReactComponent as ExportIcon } from 'images/performance-report/export.svg'
import classNames from 'classnames/bind'
import styles from './ExportButton.module.scss'
import Spinner from 'react-bootstrap/Spinner'
import Tooltip from 'react-bootstrap/Tooltip'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'

const cx = classNames.bind(styles)

const ExportButton =({ data, filename, buttonName, padding, margin, disabled, disabledTooltip }) => {
  const { CSVDownloader, Type } = useCSVDownloader()
  const [loading, setLoading] = useState(false)

  // Inline overrides take precedence over the wrapper's default padding/margin
  // so consumers can adjust spacing for their layout. Only set what's provided.
  const overrideStyle = {
    ...(padding !== undefined && { padding }),
    ...(margin !== undefined && { margin }),
  }

  useEffect(() => {
    let timeout
    if (loading) {
      timeout = setTimeout(() => {
        setLoading(false)
      }, 500)
    }

    return () => clearTimeout(timeout)
  }, [loading])

  const handleClick = () => {
    setLoading(true)
  }

  // When disabled, render a non-interactive button styled as disabled and show a
  // tooltip on hover. We avoid the native `disabled` attribute here because disabled
  // elements don't emit pointer events, which would suppress the OverlayTrigger tooltip.
  if (disabled) {
    const button = (
      <button type="button" aria-disabled="true" data-testid="wrapper">
        <div>
          <ExportIcon />
          {buttonName}
        </div>
      </button>
    )

    return (
      <div className={cx('export-button', 'disabled')} style={overrideStyle}>
        {disabledTooltip ? (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip className={cx('tooltip')}>{disabledTooltip}</Tooltip>}
          >
            {button}
          </OverlayTrigger>
        ) : (
          button
        )}
      </div>
    )
  }

  return(
    <div className={cx('export-button')} style={overrideStyle}>
      <CSVDownloader
        type={Type.Button}
        filename={filename}
        bom
        data={data}
      >
        <div onClick={handleClick} data-testid="wrapper">
          {loading ? (
            <Spinner animation="border" variant="light" />
          ) : (
            <>
              <ExportIcon />
              {buttonName}
            </>
          )}
        </div>
      </CSVDownloader>
    </div>
  )
}

ExportButton.propTypes = {
  data: PropTypes.any,
  activeTab: PropTypes.string,
  filename: PropTypes.string,
  buttonName: PropTypes.string,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  disabledTooltip: PropTypes.string,
}

export { ExportButton }
